import { marked } from 'marked';
import { HelpCircle, X } from 'lucide-react'; 

const cn = (...classes) => classes.filter(Boolean).join(' ');

const helpContentMarkdown = `
# Help & Support - Neurolaw AI 

Welcome to the Help & Support section for the NeurolawAI Legal Dashboard.

This dashboard provides several tools to assist legal professionals. Below is a brief overview of each main section:

## Dashboard Components

### Overview
This is your main landing page, providing a quick summary or access point to key features.

### Cases
Manage your legal cases. You can view, add, and organize case details here.

### My Calendar
Keep track of important dates, deadlines, and appointments related to your cases.

### Subscribe Cases
Stay updated on specific cases by subscribing to receive notifications about their status changes.

### Judgement Finder
Search for relevant legal judgements based on various criteria like case number, date range, advocate, or judge.

### Legal Research
Access tools and resources for conducting legal research.

### Document Analyzer
Upload and analyze legal documents. This tool can help extract key information, summarize content, and potentially answer questions about the document.

### Fast Draft
Utilize AI assistance to quickly draft legal documents or sections based on provided inputs.

## Frequently Asked Questions

**Q: How do I navigate between different sections?**
A: Use the sidebar on the left to access the various tools and features.

**Q: What file types are supported by the Document Analyzer?**
A: The Document Analyzer currently supports PDF and DOCX files.

**Q: How can I get help with a specific feature?**
A: This modal provides general information. For specific issues or detailed guides, please refer to the documentation or contact support.

## Contact Support

If you encounter any issues or require further assistance, please contact our support team:

Email: support@Jinvaaniiai.com
Phone: +1 (XXX) XXX-XXXX

---

*This content provides a general overview. For detailed instructions, please refer to specific feature guides.*
`;

export default function HelpAndSupportModal({ open, handleClose, darkMode = false }) { 
  const htmlContent = marked(helpContentMarkdown);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity duration-300">
      <div className={cn(
        "modal-content w-full max-w-lg rounded-xl shadow-2xl p-6 max-h-[85vh] overflow-y-auto transform transition-all duration-300 scale-95 opacity-0 animate-modal-pop-in",
        darkMode ? "bg-gray-800 text-gray-100 border border-gray-700" : "bg-white text-gray-900"
      )}>
        <div className="flex justify-between items-center mb-5 pb-3 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            <HelpCircle size={22} className={darkMode ? "text-blue-400" : "text-blue-600"}/>
            <h2 className="text-xl font-semibold">Help & Support</h2> {/* Updated Title */}
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
            <X size={20} />
          </button>
        </div>
        <div className={cn("space-y-5 text-sm md:text-base", darkMode ? "text-gray-300" : "text-gray-600")} dangerouslySetInnerHTML={{ __html: htmlContent }} />

        <button
          onClick={handleClose}
          className={cn(
            "w-full mt-6 py-2.5 rounded-lg font-semibold text-sm tracking-wide transition-colors",
            "bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          )}
        >
          Close
        </button>
      </div>
      <style jsx>{`
        @keyframes modal-pop-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-modal-pop-in {
          animation: modal-pop-in 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
